import {
	CheckCircle,
	Copy,
	ExternalLink,
	Globe,
	HelpCircle,
	Key,
	Plus,
	Settings,
	Shield,
	Trash2,
	Webhook,
	X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNodeValidation } from '../../hooks/useNodeValidation';
import { webhookApiKeysClient, type WebhookApiKey } from '../../lib/webhook-api-keys';
import type { IntegrationsNodeData } from '../../types/nodes';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';

export interface StartWebhookConfigType {
	path: string;
	authentication: 'none' | 'webhook-api-key' | 'basic-auth';
	selectedApiKeyId?: string;
	headers?: Record<string, string>;
	responseFields: { key: string; path: string; type: string }[];
	responseConfig?: {
		successStatusCode?: number;
		successMessage?: string;
		errorStatusCode?: number;
		timeout?: number;
	};
	metadata?: {
		title?: string;
		description?: string;
		source?: string;
		documentation?: string;
	};
	description?: string;
}

interface StartWebhookConfigProps {
	nodeId: string;
	data: IntegrationsNodeData;
	onChange: (data: IntegrationsNodeData) => void;
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep: () => void;
		onNextStep: () => void;
		onCompleteWizard: () => Promise<void>;
		validationAttempted?: boolean;
		validationFailed?: boolean;
	}) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

const defaultConfig: StartWebhookConfigType = {
	path: '',
	authentication: 'none',
	responseFields: [],
	responseConfig: {
		successStatusCode: 200,
		successMessage: 'Webhook processed successfully',
		errorStatusCode: 400,
		timeout: 10000,
	},
	metadata: {
		title: 'Webhook Trigger',
		description: 'Receives HTTP requests from external systems',
		source: 'custom',
	},
	description: 'Webhook trigger for external system integration',
};

const WIZARD_STEPS = [
	{ id: 1, title: 'Basic Setup', icon: Settings },
	{ id: 2, title: 'Security', icon: Shield },
	{ id: 3, title: 'Payload Fields', icon: Globe },
	{ id: 4, title: 'Response Config', icon: ExternalLink },
];

export function StartWebhookConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
}: StartWebhookConfigProps) {
	const { validateNode, isValidating, lastValidationResult, clearValidationResult } =
		useNodeValidation();

	// Wizard state
	const [currentStep, setCurrentStep] = useState(1);
	const [wizardMode, setWizardMode] = useState(true);
	const [validationAttempted, setValidationAttempted] = useState(false);
	const [validationFailed, setValidationFailed] = useState(false);

	// Component state
	const [examplePayload, setExamplePayload] = useState<string>('');
	const [isExtractingFields, setIsExtractingFields] = useState(false);

	// API key management state
	const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
	const [generatedApiKey, setGeneratedApiKey] = useState<{
		keyId: string;
		secret: string;
		username?: string;
		type?: 'api-key' | 'basic-auth';
	} | null>(null);
	const [apiKeys, setApiKeys] = useState<WebhookApiKey[]>([]);
	const [isLoadingApiKeys, setIsLoadingApiKeys] = useState(false);
	const [apiKeyName, setApiKeyName] = useState('Webhook Key');
	const [apiKeyPassword, setApiKeyPassword] = useState('');

	// Initialize config with defaults
	const config: StartWebhookConfigType = {
		...defaultConfig,
		...data.config,
		responseConfig: {
			...defaultConfig.responseConfig,
			...data.config?.responseConfig,
		},
		metadata: {
			...defaultConfig.metadata,
			...data.config?.metadata,
		},
	};

	// Debug logging to trace authentication field
	console.log('🔍 StartWebhookConfig - Component Init:', {
		nodeId,
		rawData: data,
		dataConfig: data.config,
		authentication: config.authentication,
		fullConfig: config,
	});

	const handleConfigChange = (updates: Partial<StartWebhookConfigType>) => {
		const newConfig = {
			...config,
			...updates,
		};

		console.log('🔍 StartWebhookConfig - handleConfigChange:', {
			updates,
			oldConfig: config,
			newConfig,
			authentication: newConfig.authentication,
		});

		onChange({
			...data,
			config: newConfig,
		});
	};

	// Load API keys on component mount
	useEffect(() => {
		loadApiKeys();
	}, []);

	/**
	 * Load API keys from the backend
	 */
	const loadApiKeys = async () => {
		setIsLoadingApiKeys(true);
		try {
			const keys = await webhookApiKeysClient.getApiKeys();
			setApiKeys(keys);
		} catch (error) {
			console.error('Failed to load webhook API keys:', error);
		} finally {
			setIsLoadingApiKeys(false);
		}
	};

	/**
	 * Create a new webhook credential (API key or Basic Auth)
	 */
	const createApiKey = async () => {
		try {
			const isBasicAuth = config.authentication === 'basic-auth';

			// Validate inputs for basic auth
			if (isBasicAuth && (!apiKeyName || !apiKeyPassword)) {
				alert('Please provide both username and password for Basic Auth');
				return;
			}

			const result = await webhookApiKeysClient.createApiKey({
				type: isBasicAuth ? 'basic-auth' : 'api-key',
				name: apiKeyName,
				username: isBasicAuth ? apiKeyName : undefined,
				password: isBasicAuth ? apiKeyPassword : undefined,
				description: isBasicAuth
					? `Basic Auth credentials for: ${config.path || 'webhook'}`
					: `Webhook API key for: ${config.path || 'webhook'}`,
				scopes: ['webhooks:trigger'],
				rateLimitRpm: 60,
				rateLimitRph: 1000,
			});

			setGeneratedApiKey({
				keyId: result.keyId,
				secret: result.secret,
				username: result.username,
				type: result.type,
			});
			setShowApiKeyDialog(true);

			// Clear password field for security
			if (isBasicAuth) {
				setApiKeyPassword('');
			}

			// Reload the credentials list
			await loadApiKeys();
		} catch (error) {
			console.error('Failed to create webhook credential:', error);
			alert('Failed to create webhook credential. Please try again.');
		}
	};

	/**
	 * Delete a webhook API key
	 */
	const deleteApiKey = async (id: string) => {
		if (
			!confirm(
				'Are you sure you want to delete this webhook API key? This action cannot be undone.'
			)
		) {
			return;
		}

		try {
			await webhookApiKeysClient.deleteApiKey(id);
			await loadApiKeys(); // Reload the list
		} catch (error) {
			console.error('Failed to delete webhook API key:', error);
			alert('Failed to delete webhook API key. Please try again.');
		}
	};

	const generateWebhookUrl = () => {
		const baseUrl = window.location.origin;
		return `${baseUrl}/api/v1/triggers/webhook/${config.path}`;
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	// Extract fields from example payload
	const extractFieldsFromPayload = (payloadText: string) => {
		try {
			const payload = JSON.parse(payloadText);
			const fields: { key: string; path: string; type: string }[] = [];

			const extractFromObject = (obj: unknown, path: string = '') => {
				if (!obj || typeof obj !== 'object') return;

				if (Array.isArray(obj)) {
					if (path) {
						const arrayKey = path.split('.').pop() || 'array';
						fields.push({
							key: arrayKey,
							path: path,
							type: 'array',
						});

						if (obj.length > 0) {
							const firstElement = obj[0];
							if (typeof firstElement === 'object' && firstElement !== null) {
								extractFromObject(firstElement, `${path}[0]`);
							} else {
								const elementType = typeof firstElement;
								fields.push({
									key: `${arrayKey}FirstItem`,
									path: `${path}[0]`,
									type: elementType,
								});
							}
						}
					}
				} else {
					Object.entries(obj).forEach(([key, value]) => {
						const currentPath = path ? `${path}.${key}` : key;
						const sanitizedKey = key.replace(/[^a-zA-Z0-9_]/g, '_');

						if (Array.isArray(value)) {
							fields.push({
								key: sanitizedKey,
								path: currentPath,
								type: 'array',
							});

							if (value.length > 0) {
								const firstElement = value[0];
								if (typeof firstElement === 'object' && firstElement !== null) {
									extractFromObject(firstElement, `${currentPath}[0]`);
								} else {
									const elementType = typeof firstElement;
									fields.push({
										key: `${sanitizedKey}FirstItem`,
										path: `${currentPath}[0]`,
										type: elementType,
									});
								}
							}
						} else if (typeof value === 'object' && value !== null) {
							fields.push({
								key: sanitizedKey,
								path: currentPath,
								type: 'object',
							});
							extractFromObject(value, currentPath);
						} else {
							const type = typeof value;
							fields.push({
								key: sanitizedKey,
								path: currentPath,
								type: type,
							});
						}
					});
				}
			};

			extractFromObject(payload);

			const uniqueFields = fields.filter(
				(field, index, array) => array.findIndex(f => f.path === field.path) === index
			);

			return uniqueFields.sort((a, b) => a.path.length - b.path.length);
		} catch (error) {
			console.error('Failed to parse example payload:', error);
			return [];
		}
	};

	const handleAutoExtractFields = () => {
		if (!examplePayload.trim()) return;

		setIsExtractingFields(true);
		try {
			const extractedFields = extractFieldsFromPayload(examplePayload);
			if (extractedFields.length > 0) {
				handleConfigChange({ responseFields: extractedFields });
				setExamplePayload('');
			}
		} catch (error) {
			console.error('Failed to extract fields:', error);
		} finally {
			setIsExtractingFields(false);
		}
	};

	const isValidJSON = (text: string): boolean => {
		try {
			JSON.parse(text);
			return true;
		} catch {
			return false;
		}
	};

	// Step validation
	const canProceedToStep = useCallback(
		(step: number): boolean => {
			switch (step) {
				case 1:
					return true; // Always can start
				case 2:
					return config.path.trim() !== ''; // Need path for security
				case 3:
					return config.path.trim() !== ''; // Auth is either none or webhook-api-key (managed separately)
				case 4:
					return config.path.trim() !== ''; // Can configure response if path exists
				default:
					return false;
			}
		},
		[config.path, config.authentication]
	);

	const isStepValid = (step: number) => {
		return canProceedToStep(step + 1);
	};

	const goToStep = (step: number) => {
		if (canProceedToStep(step)) {
			setCurrentStep(step);
		}
	};

	// Validation function that can be called by parent components
	const performValidation = useCallback(async (): Promise<boolean> => {
		try {
			const result = await validateNode(nodeId, data);
			return result.valid || !result.errors.some(error => error.severity === 'error');
		} catch (error) {
			console.error('Validation failed:', error);
			return false;
		}
	}, [validateNode, nodeId, data]);

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

	// Wizard state management
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode,
				currentStep,
				totalSteps: 4,
				canProceedToNext: canProceedToStep(currentStep + 1),
				onPreviousStep: () => setCurrentStep(Math.max(1, currentStep - 1)),
				onNextStep: () => setCurrentStep(Math.min(4, currentStep + 1)),
				onCompleteWizard: async () => {
					// Step 1: If validation hasn't been attempted yet, run validation and stay in node
					if (!validationAttempted) {
						try {
							const result = await validateNode(nodeId, data);
							setValidationAttempted(true);

							if (!result.valid) {
								// Validation failed - stay in node and show errors
								setValidationFailed(true);
								console.log('Validation failed, staying in node:', result);
								return; // Don't exit the node
							} else {
								// Validation succeeded - proceed to exit
								setValidationFailed(false);
								console.log('Validation succeeded:', result);
							}
						} catch (error) {
							console.error('Validation failed:', error);
							setValidationAttempted(true);
							setValidationFailed(true);
							return; // Don't exit the node on error
						}
					}

					// Step 2: If validation was already attempted, save and exit the node
					console.log('🔍 StartWebhookConfig - Saving final config:', {
						config,
						authentication: config.authentication,
					});

					// Save the current configuration before exiting
					onChange({
						...data,
						config,
					});

					setWizardMode(false);
					setTimeout(() => setCurrentStep(1), 100);

					// Reset validation state for next time
					setValidationAttempted(false);
					setValidationFailed(false);
				},
				validationAttempted,
				validationFailed,
			});
		}
	}, [
		wizardMode,
		currentStep,
		config,
		onWizardStateChange,
		onChange,
		validationAttempted,
		validationFailed,
		validateNode,
		nodeId,
		data,
		canProceedToStep,
	]);

	// Step 1: Basic Setup
	const renderStep1 = () => (
		<div className="space-y-6">
			<div className="text-center">
				<Settings className="mx-auto mb-4 w-12 h-12 text-emerald-600 dark:text-emerald-400" />
				<h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
					Basic Webhook Setup
				</h3>
				<p className="text-gray-600 dark:text-gray-400">
					Configure the basic settings for your webhook endpoint
				</p>
			</div>

			<div className="space-y-4">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Webhook Path *
					</label>
					<input
						type="text"
						value={config.path}
						onChange={e =>
							handleConfigChange({
								path: e.target.value.replace(/[^a-zA-Z0-9_.-]/g, ''),
							})
						}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
						placeholder="my-webhook"
					/>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						This will be part of your webhook URL. Only letters, numbers, dots, hyphens,
						and underscores allowed.
					</p>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Title
					</label>
					<input
						type="text"
						value={config.metadata?.title || ''}
						onChange={e =>
							handleConfigChange({
								metadata: { ...config.metadata, title: e.target.value },
							})
						}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
						placeholder="Webhook Title"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Description
					</label>
					<textarea
						value={config.metadata?.description || ''}
						onChange={e =>
							handleConfigChange({
								metadata: {
									...config.metadata,
									description: e.target.value,
								},
							})
						}
						rows={3}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
						placeholder="Describe what this webhook does"
					/>
				</div>

				{config.path && (
					<div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800">
						<div className="flex items-start">
							<Webhook className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 mr-3 flex-shrink-0" />
							<div className="flex-1">
								<p className="mb-2 text-sm font-medium text-emerald-900 dark:text-emerald-100">
									Your Webhook URL
								</p>
								<div className="flex gap-2 items-center">
									<code className="flex-1 px-3 py-2 text-xs text-emerald-900 break-all bg-white rounded border border-emerald-300 dark:bg-emerald-800/50 dark:border-emerald-700 dark:text-emerald-100">
										{generateWebhookUrl()}
									</code>
									<button
										onClick={() => copyToClipboard(generateWebhookUrl())}
										className="p-2 text-emerald-600 rounded transition-colors hover:bg-emerald-100 dark:hover:bg-emerald-800"
										title="Copy URL"
									>
										<Copy className="w-4 h-4" />
									</button>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);

	// Step 2: Security
	const renderStep2 = () => (
		<div className="space-y-6">
			<div className="text-center">
				<Shield className="mx-auto mb-4 w-12 h-12 text-emerald-600 dark:text-emerald-400" />
				<h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
					Security Configuration
				</h3>
				<p className="text-gray-600 dark:text-gray-400">
					Protect your webhook endpoint with authentication
				</p>
			</div>

			<div className="space-y-4">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Authentication Method
					</label>
					<select
						value={config.authentication}
						onChange={e =>
							handleConfigChange({
								authentication: e.target.value as
									| 'none'
									| 'webhook-api-key'
									| 'basic-auth',
							})
						}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
					>
						<option value="none">None (Public endpoint)</option>
						<option value="webhook-api-key">Webhook API Key</option>
						<option value="basic-auth">Basic Auth (Username/Password)</option>
					</select>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						{config.authentication === 'none' && 'Anyone can trigger this webhook'}
						{config.authentication === 'webhook-api-key' &&
							'Secure authentication with API keys and rate limiting'}
						{config.authentication === 'basic-auth' &&
							'HTTP Basic Authentication with username and password'}
					</p>
				</div>

				{config.authentication === 'webhook-api-key' && (
					<div className="space-y-4">
						<div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800">
							<div className="flex items-start">
								<Key className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 mr-3 flex-shrink-0" />
								<div className="flex-1">
									<p className="mb-2 text-sm font-medium text-emerald-900 dark:text-emerald-100">
										API Key Authentication
									</p>
									<p className="mb-3 text-xs text-emerald-700 dark:text-emerald-300">
										Create and manage API keys for secure webhook access. Each
										key has rate limiting and usage tracking.
									</p>
									<div className="flex gap-2">
										<input
											type="text"
											value={apiKeyName}
											onChange={e => setApiKeyName(e.target.value)}
											placeholder="Key name (e.g., Production Key)"
											className="flex-1 px-3 py-2 text-sm text-gray-900 bg-white rounded border border-emerald-300 dark:bg-emerald-800/50 dark:border-emerald-700 dark:text-white"
										/>
										<button
											onClick={createApiKey}
											className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded transition-colors hover:bg-emerald-700"
										>
											<Plus className="w-4 h-4" />
											Create Key
										</button>
									</div>
								</div>
							</div>
						</div>

						{/* API Keys List */}
						<div>
							<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
								Your Webhook API Keys
							</label>
							{isLoadingApiKeys ? (
								<p className="text-sm text-gray-500">Loading keys...</p>
							) : apiKeys.length === 0 ? (
								<p className="text-sm text-gray-500 dark:text-gray-400">
									No API keys yet. Create one above to get started.
								</p>
							) : (
								<div className="space-y-2">
									{apiKeys.map(key => (
										<div
											key={key.id}
											className="p-3 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700"
										>
											<div className="flex justify-between items-start">
												<div className="flex-1">
													<div className="flex gap-2 items-center">
														<p className="font-medium text-gray-900 dark:text-white">
															{key.name}
														</p>
														{!key.isActive && (
															<span className="px-2 py-0.5 text-xs text-red-600 bg-red-100 rounded dark:bg-red-900/20 dark:text-red-400">
																Inactive
															</span>
														)}
													</div>
													<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
														{key.description}
													</p>
													<div className="flex gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
														<span>Key: {key.keyId}</span>
														<span>RPM: {key.rateLimitRpm}</span>
														<span>RPH: {key.rateLimitRph}</span>
														<span>Used: {key.usageCount} times</span>
													</div>
												</div>
												<button
													onClick={() => deleteApiKey(key.id)}
													className="p-1.5 text-red-600 rounded transition-colors hover:bg-red-100 dark:hover:bg-red-900/20"
													title="Delete API Key"
												>
													<Trash2 className="w-4 h-4" />
												</button>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				)}

				{config.authentication === 'basic-auth' && (
					<div className="space-y-4">
						<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
							<div className="flex items-start">
								<Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
								<div className="flex-1">
									<p className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-100">
										Basic Authentication
									</p>
									<p className="mb-3 text-xs text-blue-700 dark:text-blue-300">
										Create username/password credentials for HTTP Basic
										Authentication. Credentials are encrypted and validated on
										each webhook call.
									</p>
									<div className="grid grid-cols-2 gap-3">
										<div>
											<label className="block mb-1 text-xs font-medium text-blue-900 dark:text-blue-100">
												Username
											</label>
											<input
												type="text"
												value={apiKeyName}
												onChange={e => setApiKeyName(e.target.value)}
												placeholder="webhook-user"
												className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded border border-blue-300 dark:bg-blue-800/50 dark:border-blue-700 dark:text-white"
											/>
										</div>
										<div>
											<label className="block mb-1 text-xs font-medium text-blue-900 dark:text-blue-100">
												Password
											</label>
											<input
												type="password"
												value={apiKeyPassword}
												onChange={e => setApiKeyPassword(e.target.value)}
												placeholder="Enter secure password"
												className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded border border-blue-300 dark:bg-blue-800/50 dark:border-blue-700 dark:text-white"
											/>
										</div>
									</div>
									<button
										onClick={createApiKey}
										className="flex gap-2 items-center px-4 py-2 mt-3 text-sm font-medium text-white bg-blue-600 rounded transition-colors hover:bg-blue-700"
									>
										<Plus className="w-4 h-4" />
										Create Basic Auth Credential
									</button>
								</div>
							</div>
						</div>

						{/* Basic Auth Credentials List */}
						<div>
							<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
								Your Basic Auth Credentials
							</label>
							{isLoadingApiKeys ? (
								<p className="text-sm text-gray-500">Loading credentials...</p>
							) : apiKeys.filter(key => key.type === 'basic-auth').length === 0 ? (
								<p className="text-sm text-gray-500 dark:text-gray-400">
									No Basic Auth credentials yet. Create one above to get started.
								</p>
							) : (
								<div className="space-y-2">
									{apiKeys
										.filter(key => key.type === 'basic-auth')
										.map(key => (
											<div
												key={key.id}
												className="p-3 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700"
											>
												<div className="flex justify-between items-start">
													<div className="flex-1">
														<div className="flex gap-2 items-center">
															<p className="font-medium text-gray-900 dark:text-white">
																{key.name}
															</p>
															{!key.isActive && (
																<span className="px-2 py-0.5 text-xs text-red-600 bg-red-100 rounded dark:bg-red-900/20 dark:text-red-400">
																	Inactive
																</span>
															)}
														</div>
														<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
															{key.description}
														</p>
														<div className="flex gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
															<span>Username: {key.username}</span>
															<span>RPM: {key.rateLimitRpm}</span>
															<span>RPH: {key.rateLimitRph}</span>
															<span>
																Used: {key.usageCount} times
															</span>
														</div>
													</div>
													<button
														onClick={() => deleteApiKey(key.id)}
														className="p-1.5 text-red-600 rounded transition-colors hover:bg-red-100 dark:hover:bg-red-900/20"
														title="Delete Credential"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											</div>
										))}
								</div>
							)}
						</div>
					</div>
				)}

				{config.authentication === 'none' && (
					<div className="p-4 bg-amber-50 rounded-lg border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
						<div className="flex items-start">
							<HelpCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
							<div className="text-sm text-amber-800 dark:text-amber-200">
								<p className="mb-1 font-medium">Public Endpoint Warning</p>
								<p className="text-xs">
									This webhook will be publicly accessible. Anyone with the URL
									can trigger your workflow. Consider using authentication for
									production use.
								</p>
							</div>
						</div>
					</div>
				)}

				{/* Webhook Call Example */}
				{config.path && (
					<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
						<h4 className="mb-3 text-sm font-medium text-gray-900 dark:text-gray-100">
							Example Webhook Call
						</h4>
						<div className="space-y-3">
							<div>
								<label className="block mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">
									Webhook URL
								</label>
								<div className="flex gap-2">
									<code className="flex-1 px-3 py-2 font-mono text-xs text-gray-900 bg-white rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
										{generateWebhookUrl()}
									</code>
									<button
										onClick={() => copyToClipboard(generateWebhookUrl())}
										className="px-3 py-2 text-gray-600 rounded transition-colors hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600"
										title="Copy URL"
									>
										<Copy className="w-4 h-4" />
									</button>
								</div>
							</div>

							<div>
								<label className="block mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">
									cURL Example
									{config.authentication === 'webhook-api-key' &&
										' (with API Key)'}
									{config.authentication === 'basic-auth' && ' (with Basic Auth)'}
								</label>
								<div className="relative">
									<pre className="px-3 py-3 overflow-x-auto font-mono text-xs text-gray-900 bg-white rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
										{`curl -X POST ${generateWebhookUrl()} \\${
											config.authentication === 'webhook-api-key'
												? `
  -H "x-webhook-api-key: YOUR_KEY_ID:YOUR_SECRET" \\`
												: config.authentication === 'basic-auth'
												? `
  -u USERNAME:PASSWORD \\`
												: ''
										}
  -H "Content-Type: application/json" \\
  -d '{
    "event": "example.event",
    "data": {
      "key": "value"
    }
  }'`}
									</pre>
									<button
										onClick={() =>
											copyToClipboard(
												`curl -X POST ${generateWebhookUrl()} \\${
													config.authentication === 'webhook-api-key'
														? `\n  -H "x-webhook-api-key: YOUR_KEY_ID:YOUR_SECRET" \\`
														: config.authentication === 'basic-auth'
														? `\n  -u USERNAME:PASSWORD \\`
														: ''
												}
  -H "Content-Type: application/json" \\
  -d '{
    "event": "example.event",
    "data": {
      "key": "value"
    }
  }'`
											)
										}
										className="absolute top-2 right-2 p-1.5 text-gray-600 bg-gray-100 rounded transition-colors hover:bg-gray-200 dark:text-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500"
										title="Copy cURL"
									>
										<Copy className="w-3.5 h-3.5" />
									</button>
								</div>
							</div>

							{config.authentication === 'webhook-api-key' && (
								<div className="p-3 text-xs text-blue-800 bg-blue-50 rounded border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200">
									<p className="font-medium">
										💡 Replace YOUR_KEY_ID:YOUR_SECRET with your actual webhook
										API key credentials
									</p>
								</div>
							)}

							{config.authentication === 'basic-auth' && (
								<div className="p-3 text-xs text-blue-800 bg-blue-50 rounded border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200">
									<p className="font-medium">
										💡 Replace USERNAME:PASSWORD with your Basic Auth
										credentials
									</p>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);

	// Step 3: Payload Fields
	const renderStep3 = () => (
		<div className="space-y-6">
			<div className="text-center">
				<Globe className="mx-auto mb-4 w-12 h-12 text-emerald-600 dark:text-emerald-400" />
				<h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
					Configure Payload Fields
				</h3>
				<p className="text-gray-600 dark:text-gray-400">
					Define which fields from the webhook payload you want to extract and use in your
					workflow
				</p>
			</div>

			<div className="space-y-4">
				{/* Example Payload Input */}
				<div className="p-4 bg-amber-50 rounded-lg border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
					<h4 className="mb-3 font-medium text-amber-900 dark:text-amber-100">
						Quick Setup: Paste Example Payload
					</h4>
					<p className="mb-3 text-sm text-amber-700 dark:text-amber-300">
						Paste a sample webhook payload below to automatically generate response
						fields.
					</p>

					<div className="space-y-3">
						<textarea
							value={examplePayload}
							onChange={e => setExamplePayload(e.target.value)}
							placeholder={`{
  "event": "user.created",
  "data": {
    "userId": "12345",
    "email": "user@example.com",
    "name": "John Doe"
  },
  "timestamp": "2024-01-15T10:30:00Z"
}`}
							rows={6}
							className="px-3 py-2 w-full font-mono text-sm text-gray-900 bg-white rounded-md border border-amber-300 dark:border-amber-600 dark:bg-gray-700 dark:text-white"
						/>

						<div className="flex justify-between items-center">
							<button
								onClick={handleAutoExtractFields}
								disabled={
									!examplePayload.trim() ||
									!isValidJSON(examplePayload) ||
									isExtractingFields
								}
								className={`px-4 py-2 rounded-md flex items-center gap-2 transition-colors ${
									!examplePayload.trim() || !isValidJSON(examplePayload)
										? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
										: 'bg-amber-600 hover:bg-amber-700 text-white'
								}`}
							>
								{isExtractingFields ? (
									<>
										<div className="w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
										Extracting...
									</>
								) : (
									<>
										<Plus className="w-4 h-4" />
										Auto-Extract Fields
									</>
								)}
							</button>

							{examplePayload.trim() && (
								<button
									onClick={() => setExamplePayload('')}
									className="px-3 py-2 text-amber-600 rounded-md transition-colors hover:bg-amber-100 dark:hover:bg-amber-900/30"
								>
									Clear
								</button>
							)}
						</div>
					</div>
				</div>

				{/* Extracted Fields Preview */}
				{config.responseFields.length > 0 && (
					<div className="p-4 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
						<h4 className="mb-3 font-medium text-green-900 dark:text-green-100">
							Extracted Fields ({config.responseFields.length})
						</h4>
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
							{config.responseFields.map((field, index) => (
								<div
									key={index}
									className="p-3 bg-white rounded border border-green-200 dark:bg-green-800/50 dark:border-green-700"
								>
									<div className="flex justify-between items-center mb-2">
										<span className="text-sm font-medium text-green-900 dark:text-green-100">
											{field.key}
										</span>
										<span
											className={`px-2 py-1 text-xs rounded-full ${
												field.type === 'string'
													? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
													: field.type === 'number'
													? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
													: field.type === 'boolean'
													? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
													: field.type === 'array'
													? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
													: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
											}`}
										>
											{field.type}
										</span>
									</div>
									<div className="font-mono text-xs text-green-700 dark:text-green-300">
										{field.path}
									</div>
									<div className="mt-1 text-xs text-green-600 dark:text-green-400">
										Use as:{' '}
										<code className="px-1 font-mono bg-green-100 rounded dark:bg-green-700">{`{{${field.key}}}`}</code>
									</div>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Manual Field Configuration */}
				<div className="space-y-4">
					<div className="flex justify-between items-center">
						<h4 className="font-medium text-gray-900 dark:text-white">
							Manual Field Configuration
						</h4>
						<button
							onClick={() => {
								const newFields = [
									...config.responseFields,
									{ key: '', path: '', type: 'string' },
								];
								handleConfigChange({ responseFields: newFields });
							}}
							className="flex gap-1 items-center px-3 py-1 text-sm text-white bg-blue-600 rounded transition-colors hover:bg-blue-700"
						>
							<Plus className="w-3 h-3" />
							Add Field
						</button>
					</div>

					{config.responseFields.map((field, index) => (
						<div
							key={index}
							className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700"
						>
							<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
								<div className="space-y-3 lg:col-span-2">
									<div>
										<label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
											JSON Path <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={field.path}
											onChange={e => {
												const newFields = [...config.responseFields];
												newFields[index] = {
													...field,
													path: e.target.value,
												};
												handleConfigChange({ responseFields: newFields });
											}}
											placeholder="$.event"
											className="px-3 py-2 w-full text-sm placeholder-gray-500 text-gray-900 bg-white rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 focus:border-blue-500 dark:focus:border-blue-400 dark:border-gray-600"
										/>
									</div>

									<div>
										<label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
											Variable Name <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={field.key}
											onChange={e => {
												const newFields = [...config.responseFields];
												newFields[index] = {
													...field,
													key: e.target.value,
												};
												handleConfigChange({ responseFields: newFields });
											}}
											placeholder="eventType"
											className="px-3 py-2 w-full text-sm placeholder-gray-500 text-gray-900 bg-white rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 focus:border-blue-500 dark:focus:border-blue-400 dark:border-gray-600"
										/>
									</div>
								</div>

								<div className="space-y-3">
									<div>
										<label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
											Data Type
										</label>
										<select
											value={field.type}
											onChange={e => {
												const newFields = [...config.responseFields];
												newFields[index] = {
													...field,
													type: e.target.value,
												};
												handleConfigChange({ responseFields: newFields });
											}}
											className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:border-blue-500 dark:focus:border-blue-400"
										>
											<option value="string">Text</option>
											<option value="number">Number</option>
											<option value="boolean">True/False</option>
											<option value="object">Object</option>
											<option value="array">List</option>
										</select>
									</div>

									<button
										onClick={() => {
											const newFields = config.responseFields.filter(
												(_, i) => i !== index
											);
											handleConfigChange({ responseFields: newFields });
										}}
										className="px-3 py-2 w-full text-red-600 rounded transition-colors hover:bg-red-50 dark:hover:bg-red-900/30"
									>
										Remove Field
									</button>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);

	// Step 4: Response Configuration
	const renderStep4 = () => (
		<div className="space-y-6">
			<div className="text-center">
				<ExternalLink className="mx-auto mb-4 w-12 h-12 text-emerald-600 dark:text-emerald-400" />
				<h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
					Response Configuration
				</h3>
				<p className="text-gray-600 dark:text-gray-400">
					Configure how your webhook responds to incoming requests
				</p>
			</div>

			<div className="space-y-4">
				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
							Success Status Code
						</label>
						<input
							type="number"
							min={200}
							max={299}
							value={config.responseConfig?.successStatusCode || 200}
							onChange={e =>
								handleConfigChange({
									responseConfig: {
										...config.responseConfig,
										successStatusCode: parseInt(e.target.value) || 200,
									},
								})
							}
							className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
						/>
					</div>

					<div>
						<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
							Error Status Code
						</label>
						<input
							type="number"
							min={400}
							max={599}
							value={config.responseConfig?.errorStatusCode || 400}
							onChange={e =>
								handleConfigChange({
									responseConfig: {
										...config.responseConfig,
										errorStatusCode: parseInt(e.target.value) || 400,
									},
								})
							}
							className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
						/>
					</div>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Success Message
					</label>
					<input
						type="text"
						value={config.responseConfig?.successMessage || ''}
						onChange={e =>
							handleConfigChange({
								responseConfig: {
									...config.responseConfig,
									successMessage: e.target.value,
								},
							})
						}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
						placeholder="Webhook processed successfully"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Timeout (milliseconds)
					</label>
					<input
						type="number"
						min={1000}
						max={30000}
						value={config.responseConfig?.timeout || 10000}
						onChange={e =>
							handleConfigChange({
								responseConfig: {
									...config.responseConfig,
									timeout: parseInt(e.target.value) || 10000,
								},
							})
						}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
					/>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						Maximum time to wait for workflow completion before responding
					</p>
				</div>

				<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
					<div className="flex items-start">
						<HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
						<div className="text-sm text-blue-800 dark:text-blue-200">
							<p className="mb-1 font-medium">Response Behavior</p>
							<ul className="space-y-1 text-xs list-disc list-inside">
								<li>Success responses use the success status code and message</li>
								<li>Errors and timeouts use the error status code</li>
								<li>The timeout prevents webhook callers from waiting too long</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<div className="flex flex-col h-full">
			{/* Wizard Steps Header */}
			<div className="flex bg-gray-50 border-b border-gray-200 transition-colors dark:bg-gray-800 dark:border-gray-700">
				{WIZARD_STEPS.map(step => {
					const isActive = currentStep === step.id;
					const isCompleted = isStepValid(step.id);
					const canAccess = canProceedToStep(step.id);
					const Icon = step.icon;

					return (
						<button
							key={step.id}
							disabled={!canAccess}
							className={`flex-1 px-3 py-3 text-xs font-medium transition-colors ${
								isActive
									? 'text-emerald-600 bg-white border-b-2 border-emerald-500 dark:border-emerald-400 dark:text-emerald-400 dark:bg-gray-700'
									: canAccess
									? 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
									: 'text-gray-400 cursor-not-allowed dark:text-gray-500'
							}`}
							onClick={() => canAccess && goToStep(step.id)}
						>
							<div className="flex flex-col gap-1 items-center">
								<div className="flex gap-1 items-center">
									<Icon className="w-4 h-4" />
									{isCompleted && (
										<CheckCircle className="w-3 h-3 text-green-500" />
									)}
								</div>
								<span className="leading-tight text-center">{step.title}</span>
							</div>
						</button>
					);
				})}
			</div>

			{/* Step Content */}
			<div className="overflow-auto flex-1 p-6 bg-white transition-colors dark:bg-gray-900">
				{/* Validation Results */}
				{(isValidating || lastValidationResult) && (
					<ValidationResultDisplay
						errors={lastValidationResult?.errors || []}
						isValidating={isValidating}
						onDismiss={clearValidationResult}
					/>
				)}

				{currentStep === 1 && renderStep1()}
				{currentStep === 2 && renderStep2()}
				{currentStep === 3 && renderStep3()}
				{currentStep === 4 && renderStep4()}
			</div>

			{/* API Key Generation Dialog */}
			{showApiKeyDialog && generatedApiKey && (
				<div className="flex fixed inset-0 z-50 justify-center items-center bg-black/50">
					<div className="relative p-6 w-full max-w-2xl bg-white rounded-lg shadow-xl dark:bg-gray-800">
						<button
							onClick={() => {
								setShowApiKeyDialog(false);
								setGeneratedApiKey(null);
							}}
							className="absolute top-4 right-4 p-1 text-gray-400 rounded-full transition-colors hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
						>
							<X className="w-5 h-5" />
						</button>

						<div className="mb-4">
							<div className="flex gap-2 items-center mb-2">
								{generatedApiKey.type === 'basic-auth' ? (
									<Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
								) : (
									<Key className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
								)}
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
									{generatedApiKey.type === 'basic-auth'
										? 'Basic Auth Credentials Created'
										: 'Webhook API Key Created'}
								</h3>
							</div>
							<p className="text-sm text-gray-600 dark:text-gray-400">
								Your{' '}
								{generatedApiKey.type === 'basic-auth'
									? 'Basic Auth credentials have'
									: 'webhook API key has'}{' '}
								been successfully created. Save the{' '}
								{generatedApiKey.type === 'basic-auth' ? 'password' : 'secret'} now
								- you won't be able to see it again!
							</p>
						</div>

						<div className="space-y-4">
							{generatedApiKey.type === 'basic-auth' && generatedApiKey.username && (
								<div>
									<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Username
									</label>
									<div className="flex gap-2">
										<code className="flex-1 px-3 py-2 font-mono text-sm text-gray-900 bg-gray-50 rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
											{generatedApiKey.username}
										</code>
										<button
											onClick={() =>
												copyToClipboard(generatedApiKey.username || '')
											}
											className="px-3 py-2 text-blue-600 rounded transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
											title="Copy Username"
										>
											<Copy className="w-4 h-4" />
										</button>
									</div>
								</div>
							)}

							{generatedApiKey.type !== 'basic-auth' && (
								<div>
									<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Key ID
									</label>
									<div className="flex gap-2">
										<code className="flex-1 px-3 py-2 font-mono text-sm text-gray-900 bg-gray-50 rounded border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
											{generatedApiKey.keyId}
										</code>
										<button
											onClick={() => copyToClipboard(generatedApiKey.keyId)}
											className="px-3 py-2 text-emerald-600 rounded transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
											title="Copy Key ID"
										>
											<Copy className="w-4 h-4" />
										</button>
									</div>
								</div>
							)}

							<div>
								<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
									{generatedApiKey.type === 'basic-auth' ? 'Password' : 'Secret'}{' '}
									(Save this now!)
								</label>
								<div className="flex gap-2">
									<code className="flex-1 px-3 py-2 font-mono text-sm text-gray-900 break-all bg-amber-50 rounded border-2 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-100">
										{generatedApiKey.secret}
									</code>
									<button
										onClick={() => copyToClipboard(generatedApiKey.secret)}
										className="px-3 py-2 text-amber-600 rounded transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/20"
										title={
											generatedApiKey.type === 'basic-auth'
												? 'Copy Password'
												: 'Copy Secret'
										}
									>
										<Copy className="w-4 h-4" />
									</button>
								</div>
								<p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
									⚠️ This{' '}
									{generatedApiKey.type === 'basic-auth' ? 'password' : 'secret'}{' '}
									will only be shown once. Copy it now and store it securely.
								</p>
							</div>

							<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
								<p className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-100">
									{generatedApiKey.type === 'basic-auth'
										? 'Using Your Basic Auth Credentials'
										: 'Using Your API Key'}
								</p>
								<p className="mb-3 text-xs text-blue-700 dark:text-blue-300">
									Include this header in your webhook requests:
								</p>
								<code className="block px-3 py-2 font-mono text-xs text-blue-900 break-all bg-white rounded border border-blue-300 dark:bg-blue-800/50 dark:border-blue-700 dark:text-blue-100">
									{generatedApiKey.type === 'basic-auth'
										? `Authorization: Basic ${btoa(
												`${generatedApiKey.username}:${generatedApiKey.secret}`
										  )}`
										: `x-webhook-api-key: ${generatedApiKey.keyId}:${generatedApiKey.secret}`}
								</code>
								{generatedApiKey.type === 'basic-auth' && (
									<p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
										Or use with cURL:{' '}
										<code className="text-blue-900 dark:text-blue-100">
											-u {generatedApiKey.username}:{generatedApiKey.secret}
										</code>
									</p>
								)}
							</div>
						</div>

						<div className="flex gap-3 justify-end mt-6">
							<button
								onClick={() => {
									let credentials: string;
									if (generatedApiKey.type === 'basic-auth') {
										// Basic Auth format
										const base64Creds = btoa(
											`${generatedApiKey.username}:${generatedApiKey.secret}`
										);
										credentials = `Username: ${generatedApiKey.username}\nPassword: ${generatedApiKey.secret}\nHeader: Authorization: Basic ${base64Creds}\n\ncURL: -u ${generatedApiKey.username}:${generatedApiKey.secret}`;
									} else {
										// API Key format
										credentials = `Key ID: ${generatedApiKey.keyId}\nSecret: ${generatedApiKey.secret}\nHeader: x-webhook-api-key: ${generatedApiKey.keyId}:${generatedApiKey.secret}`;
									}
									copyToClipboard(credentials);
								}}
								className={`flex gap-2 items-center px-4 py-2 font-medium text-white rounded transition-colors ${
									generatedApiKey.type === 'basic-auth'
										? 'bg-blue-600 hover:bg-blue-700'
										: 'bg-emerald-600 hover:bg-emerald-700'
								}`}
							>
								<Copy className="w-4 h-4" />
								Copy All Credentials
							</button>
							<button
								onClick={() => {
									setShowApiKeyDialog(false);
									setGeneratedApiKey(null);
								}}
								className="px-4 py-2 font-medium text-gray-700 bg-gray-100 rounded transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
